import { cn } from '@/lib/utils';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  FileSpec,
  IStep,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';
import type { IMode, IModeOption } from '@chainlit/react-client';
import { modesState } from '@chainlit/react-client';

import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'components/i18n/Translator';

import { useQuery } from '@/hooks/query';
import { useIsMobile } from '@/hooks/use-mobile';

import { chatSettingsOpenState } from '@/state/project';
import { persistentCommandState } from 'state/chat';

import CommandButtons from './CommandButtons';
import CommandButton from './CommandPopoverButton';
import Input, { InputMethods } from './Input';
import McpButton from './Mcp';
import ModePicker from './ModePicker';
import SubmitButton from './SubmitButton';
import VoiceButton from './VoiceButton';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
  variant?: 'default' | 'compact';
}

export default function MessageComposer({
  fileSpec: _fileSpec,
  onFileUpload: _onFileUpload,
  onFileUploadError: _onFileUploadError,
  autoScrollRef,
  variant = 'default'
}: Props) {
  const isCompact = variant === 'compact';
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);
  const { t } = useTranslation();

  const { user } = useAuth();
  const { sendMessage, replyMessage } = useChatInteract();
  const { askUser, chatSettingsInputs, disabled: _disabled } = useChatData();

  const disabled = _disabled;

  const isMobile = useIsMobile();

  // Get/set available modes from state - selections are tracked via the 'default' flag on options
  const [modes, setModes] = useRecoilState(modesState);

  const handleModeSelect = useCallback(
    (modeId: string, optionId: string) => {
      setModes((prevModes) =>
        prevModes.map((mode) => {
          if (mode.id !== modeId) return mode;
          return {
            ...mode,
            options: mode.options.map((opt: IModeOption) => ({
              ...opt,
              default: opt.id === optionId
            }))
          };
        })
      );
    },
    [setModes]
  );

  // Helper to get selected option for a mode (the one with default=true, or first option)
  const getSelectedOptionId = useCallback((mode: IMode): string | undefined => {
    const defaultOpt = mode.options.find((opt) => opt.default);
    return defaultOpt?.id || mode.options[0]?.id;
  }, []);

  let promptValue = '';
  try {
    const query = useQuery();
    promptValue = query.get('prompt') || '';
  } catch {
    console.warn('Could not parse query parameters');
  }

  const [promptUsed, setPromptUsed] = useState(false);

  const onPaste = useCallback((_event: ClipboardEvent) => {
    // Disabled file pasting as per user request
  }, []);

  const onSubmit = useCallback(
    async (msg: string, selectedCommand?: string) => {
      // Build modes dict: only include modes that have selections
      const modesDict: Record<string, string> = {};
      modes.forEach((mode) => {
        const selectedId = getSelectedOptionId(mode);
        if (selectedId) {
          modesDict[mode.id] = selectedId;
        }
      });

      const message: IStep = {
        threadId: '',
        command: selectedCommand,
        modes: Object.keys(modesDict).length > 0 ? modesDict : undefined,
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message);
    },
    [user, sendMessage, autoScrollRef, modes, getSelectedOptionId]
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage, autoScrollRef]
  );

  const submit = useCallback(() => {
    if (disabled || (value.trim() === '' && !selectedCommand)) {
      return;
    }

    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, selectedCommand?.id);
    }

    setValue(''); // Clear the value state
    inputRef.current?.reset();
  }, [value, disabled, askUser, selectedCommand, onSubmit, onReply]);

  useEffect(() => {
    if (inputRef.current && promptValue && !promptUsed) {
      const prompt = promptValue;
      if (prompt) {
        if (prompt.length > 1000) {
          inputRef.current?.setValueExtern(prompt.slice(0, 1000));
        } else {
          inputRef.current?.setValueExtern(prompt);
        }
        setPromptUsed(true);
      }
    }
  }, [promptValue, promptUsed]);

  return (
    <div
      id="message-composer"
      className={cn(
        'group/composer flex w-full border border-border/60 bg-card/95 transition-[border-color,box-shadow] duration-200 ease-out focus-within:border-foreground/15 focus-within:ring-[3px] focus-within:ring-primary/10',
        isCompact
          ? 'min-h-14 flex-row items-end gap-2 rounded-[14px] px-2.5 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)] focus-within:shadow-[0_6px_18px_-14px_rgba(15,23,42,0.24)]'
          : 'min-h-[84px] flex-col rounded-2xl px-3 py-2.5 shadow-[0_2px_10px_-7px_rgba(15,23,42,0.18)] focus-within:shadow-[0_6px_20px_-14px_rgba(15,23,42,0.24)]'
      )}
    >
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus={!isMobile}
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        placeholder={t('chat.input.placeholder')}
        className={cn(
          'px-1 text-[14px] leading-6 placeholder:text-[14px] placeholder:text-muted-foreground/70',
          isCompact ? 'py-0.5' : 'py-1.5'
        )}
      />
      <div
        className={cn(
          'flex items-center justify-between',
          isCompact ? 'shrink-0 self-end' : 'mt-auto pt-1'
        )}
      >
        <div className="-ml-1 flex min-w-0 items-center gap-0.5">
          <VoiceButton disabled={disabled} />
          {chatSettingsInputs.length > 0 && (
            <Button
              id="chat-settings-open-modal"
              disabled={disabled}
              onClick={() => setChatSettingsOpen(true)}
              className="size-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              variant="ghost"
              size="icon"
            >
              <Settings className="!size-6" />
            </Button>
          )}
          <McpButton disabled={disabled} />
          {modes.map((mode) => (
            <ModePicker
              key={mode.id}
              mode={mode}
              disabled={disabled}
              selectedOptionId={getSelectedOptionId(mode)}
              onOptionSelect={handleModeSelect}
            />
          ))}
          <CommandButton
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />
          <CommandButtons
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />
        </div>
        <div
          className={cn(
            'flex shrink-0 items-center gap-1',
            !isCompact && 'ml-2'
          )}
        >
          <SubmitButton
            onSubmit={submit}
            disabled={disabled || (!value.trim() && !selectedCommand)}
          />
        </div>
      </div>
    </div>
  );
}
